import { BlogPost } from '@adapters/prismic/types/blogPost';
import {
  BrandedStoreSliceType,
  HomepageValueProp,
} from '@adapters/prismic/types/brandedStorePage';
import { Copy } from './brandedStorePage';

export enum PageType {
  homePage = 'homePage',
  landingPage = 'landingPage',
  blogPage = 'blogPage',
  brandedStorePage = 'brandedStorePage',
  categoryInfoPage = 'CIP',
}

/** *********************** Generic ****************************************** */
export interface GenericSlice {
  id?: string;
  slice_type: string;
  primary: any;
  items?: any;
  newItems?: any;
  blog_posts?: BlogPost[];
}

/** *********************** Hero Image *************************************** */
export interface HeroImageSliceData {
  image: {
    url: string;
    alt: string;
    dimensions: {
      width: number;
      height: number;
    };
  };
  link?: string;
  heading: string;
  button_text?: string;
  content?: string;
  trackPromos?: boolean;
  pageType: PageType;
  sliceIndex?: number;
}

export interface HeroImageSlice {
  id?: string;
  slice_type: 'hero_image';
  primary: HeroImageSliceData;
}

/** *********************** Hero Banner *************************************** */
export interface HeroBannerSliceData {
  media_type: 'image' | 'video';
  mobile_image: {
    url: string;
    alt: string;
    dimensions: {
      width: number;
      height: number;
    };
  };
  desktop_image: {
    url: string;
    alt: string;
    dimensions: {
      width: number;
      height: number;
    };
  };
  mobile_video: string;
  desktop_video: string;
  video_alt_text?: string;
  url: string;
  black_text?: string;
  red_text?: string;
  red_text_placement?: boolean;
  red_text_new_line?: boolean;
  body_copy?: {
    text: string;
  }[];
  cta_text?: string;
  cta_type?: 'primary' | 'secondary' | 'tertiary';
  track_promos?: boolean;
}

export interface HeroBannerSlice {
  id?: string;
  slice_type: 'hero_banner';
  primary: HeroBannerSliceData;
}

/** *********************** Primary Promo Boxes ************************************* */
export interface PromoBoxItem {
  image: {
    url: string;
    alt: string;
    dimensions: {
      width: number;
      height: number;
    };
  };
  url: string;
  heading_black_text?: string;
  heading_red_text?: string;
  red_text_next_line?: boolean;
  heading?: string;
  body?: string;
  image_url?: string;
  cta_text?: string;
  first_cta_text?: string;
  first_cta_url?: string;
  second_cta_text?: string;
  second_cta_url?: string;
  'co-op_brand_attribute'?: string | null;
}

export interface PrimaryPromoBoxesSliceData {
  id?: string;
  slice_type: 'primary_promo_boxes';
  primary: {
    title?: string;
    center_align?: boolean;
  };
  items: PromoBoxItem[];
}

/** *********************** Secondary Promo Boxes ************************************* */

export interface SecondaryPromoBoxesSliceData {
  id?: string;
  slice_type: 'secondary_promo_boxes';
  primary: {
    title?: string;
  };
  items: PromoBoxItem[];
}

/** *********************** Recommendations ************************************* */
export interface RecommendationsSlice {
  id?: string;
  slice_type: 'recommendations';
  primary: Record<string, never>;
  items: [];
}

/** *********************** Algonomy Placement ************************************* */
export interface AlgonomyPlacementSlice {
  id?: string;
  slice_type: 'algonomy_placement';
  primary: {
    placement_name?: string;
    category?: string | null;
    co_op_brand?: string | null;
  };
  items: [];
}

/** *********************** Recommendations Home 1 ****************************** */
export interface RecommendationsHome1Slice {
  id?: string;
  slice_type: 'recommendations_home1';
  primary: Record<string, never>;
  items: [];
}

/** *********************** Recommendations Home 3 ****************************** */
export interface RecommendationsHome3Slice {
  id?: string;
  slice_type: 'recommendations_home3';
  primary: Record<string, never>;
  items: [];
}

/** *********************** Category Nav Carousel ****************************** */
export interface CategoryNavCarouselItem {
  image: {
    url: string;
    alt: string;
    dimensions: {
      width: number;
      height: number;
    };
  };
  url: string;
  heading?: string;
  body?: Copy;
  cta_text?: string;
}

export interface CategoryNavCarouselSliceData {
  id?: string;
  slice_type: 'category_nav_carousel';
  primary: {
    title?: string;
  };
  items: CategoryNavCarouselItem[];
}

/** *********************** Image Banner ************************************* */
export enum ImageBannerTextAlignment {
  Left = 'Left',
  Center = 'Center',
}

export interface ImageBannerSliceData {
  image: {
    url: string;
    alt: string;
    dimensions: {
      width: number;
      height: number;
    };
  };
  title: string;
  button_text?: string;
  button_link?: string;
  content?: string;
  text_alignment?: ImageBannerTextAlignment;
  trackPromos?: boolean;
  pageType?: PageType;
  sliceIndex?: number;
}

export interface ImageBannerSlice {
  id?: string;
  slice_type: 'image_banner';
  primary: ImageBannerSliceData;
}

/** *********************** Value Prop *************************************** */
export interface ValuePropsSlice {
  id?: string;
  slice_type: 'value_props';
  primary?: any; // TODO: Expand type when implementing
}

export interface HomepageValuePropSlice {
  id?: string;
  slice_type?: BrandedStoreSliceType.valueProps;
  primary?: {
    id?: string;
    number_of_cards?: string;
  };
  items?: HomepageValueProp[];
}

/** *********************** Link Card Grid *********************************** */
export interface LinkCardGridSliceData {
  card_image: {
    url: string;
    alt: string;
    dimensions: {
      width: number;
      height: number;
    };
  };
  button_text?: string;
  sub_text?: string;
  link_url: string;
}

export interface LinkCardGridSlice {
  id?: string;
  slice_type: 'link_card_grid';
  items: LinkCardGridSliceData[];
}

/** *********************** Category Navigation ****************************** */
export interface CategoryNavImage {
  url: string;
  alt: string;
  dimensions: {
    width: number;
    height: number;
  };
}

export interface CategoryNavLinks {
  category_nav_list_link: string;
  category_nav_list_text: string;
}

export interface CategoryNavigationSliceData {
  category_image?: CategoryNavImage;
  category_title?: string;
  category_nav_links?: CategoryNavLinks[];
  button_text?: string;
  button_link?: string;
}

export interface CategoryNavigationSlice {
  id?: string;
  slice_type: 'category_navigation';
  items: {
    category_nav_list: {
      data: CategoryNavigationSliceData;
    };
  }[];
}

/** *********************** Grid Icon **************************************** */
export interface GridIcon {
  image: {
    url: string;
    alt: string;
    dimensions: {
      width: number;
      height: number;
    };
  };
  link: string;
}

export interface IconGridSlice {
  id?: string;
  slice_type: 'featured_brands';
  primary: {
    section_title: string;
  };
  items: GridIcon[];
}

/** *********************** Markdown Copy ************************************ */
export interface RichTextEntry {
  text: string;
  className?: string;
}

export interface MarkdownCopySlice {
  id?: string;
  slice_type: 'markdowncopy';
  primary: {
    id?: string;
    copy: RichTextEntry[];
  };
}

/** *********************** Image With Caption ******************************* */
export interface ImageWithCaptionSliceData {
  id?: string;
  image?: {
    dimensions: {
      width: number;
      height: number;
    };
    url: string;
    alt: string;
  };
  caption?: string;
  link?: {
    url: string;
  };
  fullWidthImage?: boolean;
}

export interface ImageWithCaptionSlice {
  id?: string;
  slice_type: 'image';
  primary: ImageWithCaptionSliceData;
}

/** *********************** Image With Block ********************************* */
export interface ImageWithBlockSliceData {
  id?: string;
  image?: {
    dimensions: {
      width: number;
      height: number;
    };
    url: string;
    alt?: string;
  };
  copy?: RichTextEntry[];
  link?: {
    url?: string;
  };
}

export interface ImageWithBlockSlice {
  id?: string;
  slice_type: 'vertimage';
  primary: ImageWithBlockSliceData;
}

/** *********************** Action ******************************************* */
export interface ActionsSliceData {
  label: string;
  link: string;
  importance: 'primary' | 'normal';
}

export interface ActionsSlice {
  id?: string;
  slice_type: 'action';
  items: ActionsSliceData[];
  primary: {
    id?: string;
  };
}

/** *********************** Media Embed ************************************** */
export interface MediaEmbedSliceData {
  id?: string;
  embed: {
    provider_name: 'YouTube' | 'Instagram' | 'Vimeo';
    html: string;
    height: number;
    width: number;
  };
}

export interface MediaEmbedSlice {
  id?: string;
  slice_type: 'mediaembed';
  primary: MediaEmbedSliceData;
}

/** *********************** IFrame Embed ************************************* */
export interface IFrameEmbedSliceData {
  url: string;
  title: string;
  height: number;
  width: number;
}

export interface IFrameEmbedSlice {
  id?: string;
  slice_type: 'iframeembed';
  primary: IFrameEmbedSliceData;
}

/** *********************** Link Tiles *************************************** */
export interface LinkTile {
  tile_image: {
    url: string;
    alt: string;
    dimensions: {
      width: number;
      height: number;
    };
  };
  tile_link: {
    url: string;
  };
}

export interface LinkTilesSlice {
  id?: string;
  slice_type: 'link_tiles';
  items: LinkTile[];
}

/** *********************** Inline Alert ************************************* */
export interface InlineAlertSliceData {
  text: string;
  messageType?: 'INFORMATION' | 'WARNING' | 'ERROR' | 'SUCCESS';
}

export interface InlineAlertSlice {
  id?: string;
  slice_type: 'inlinealert';
  primary: InlineAlertSliceData;
}

/** *********************** Featured Blog Posts ************************************* */
export interface FeaturedBlogPostsSliceData {
  heading: string;
}

export interface FeatureBlogPostsTransformSlice {
  slice_type: 'featured_blog_posts';
  primary: FeaturedBlogPostsSliceData;
}

export interface FeaturedBlogPostsSlice {
  id?: string;
  slice_type: 'featured_blog_posts';
  primary: FeaturedBlogPostsSliceData;
  blog_posts?: BlogPost[];
  items?: Array<{ blog_post: BlogPost }>;
}

/** *********************** Form Embed ************************************* */
export interface FormEmbedSliceData {
  [x: string]: string;
  html: string;
}

export interface FormEmbedSlice {
  id?: string;
  slice_type: 'formembed';
  primary: FormEmbedSliceData;
}

/** *********************** Drop Down ************************************* */
export interface DropDownSliceData {
  title: string;
  copy: string;
  isOpen: boolean;
}

export interface DropDownSlice {
  id?: string;
  slice_type: 'dropdown';
  primary: DropDownSliceData;
}
